import { Link } from "@inertiajs/react"

import Layout from "../../../shared/Layout"

interface Pill {
  label: string
  href: string
  newTab: boolean
}

interface ContactCard {
  id: number
  name: string
  note: string | null
  pills: Pill[]
  // t017 `impl-contacts-own-card` (FR-7.2, FR-7.3): `/profile` on the
  // viewer's own card, `null` on every other -- decided server-side from
  // the user link alone; this page never compares ids to work it out.
  profileHref: string | null
}

interface ContactSection {
  heading: string
  contacts: ContactCard[]
}

interface ContactsProps {
  sections: ContactSection[]
  emptyText: string
  // t017 `impl-contacts-own-card` (FR-7.2): the own-card «Изменить»
  // link's one shared label -- not repeated per card.
  editLabel: string
}

const CARD_CLASSES = "rounded-xl border border-gray-200 p-3.5"
const PILL_CLASSES =
  "inline-flex items-center rounded-full bg-accent/5 px-3.5 py-2 text-[13px] font-medium leading-[18px] text-accent"
const CALL_PILL_CLASSES = `${PILL_CLASSES} gap-1.5`

// FR-3.6: the call pill's own icon — the design contract's one pilled
// icon (docs/design/contacts.html:110-113); WhatsApp/Telegram carry no
// icon, text only.
function CallIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 2.5h2.2L6.4 5.6l-1.5 1.2a10 10 0 004.3 4.3l1.2-1.5 3.1 1.2v2.2c0 .6-.5 1-1 1C7 14 2 9 2 3.5c0-.5.4-1 1-1z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// FR-3.5, FR-3.6: `newTab` is what distinguishes the call pill (the only
// channel that opens in the same tab, so it alone carries neither
// `target` nor `rel`, and it alone carries the phone icon) from
// WhatsApp/Telegram, which both open in a new tab with `rel="noopener"`.
function ContactPill({ pill }: { pill: Pill }) {
  const external = pill.newTab ? { target: "_blank", rel: "noopener" } : {}

  return (
    <a
      href={pill.href}
      className={pill.newTab ? PILL_CLASSES : CALL_PILL_CLASSES}
      {...external}
    >
      {!pill.newTab && <CallIcon />}
      {pill.label}
    </a>
  )
}

const NAME_CLASSES = "text-[15px] font-semibold leading-[20px] text-gray-900"

// t017 `impl-contacts-own-card` (FR-7.2, FR-7.5): only the viewer's own
// card (`profileHref` non-null) wraps the name in the design's flex row
// with the accent «Изменить» link (docs/design/contacts.html:154-158);
// every other card renders the bare name `<p>`, byte-identical to before
// this task (contacts.html:122-124) -- `Link`, not a plain `<a>`, since
// /profile is an internal Inertia route (inertia-react-standards).
function NameRow({ contact, editLabel }: { contact: ContactCard; editLabel: string }) {
  if (contact.profileHref === null) {
    return <p className={NAME_CLASSES}>{contact.name}</p>
  }

  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className={NAME_CLASSES}>{contact.name}</p>
      <Link href={contact.profileHref} className="text-[13px] font-medium leading-[18px] text-accent">
        {editLabel}
      </Link>
    </div>
  )
}

// The public, read-only contact list — the «Контакты» tab's own page
// (Contacts::ContactsController#index renders it). Props mirror exactly
// what the controller sends: every pill's label and href already
// resolved server-side (FR-2.9, no tel:/wa.me/t.me construction here),
// and no channel value or bare user_id anywhere in the payload (NFR-7,
// AC-14). Implements docs/design/contacts.html's Block 1 verbatim: the
// section list (FR-3.2, FR-3.3, computed server-side by
// Contacts::GroupByCategory) or, with none at all, the single empty-state
// line (FR-3.7) — no section chrome in that state.
export default function Contacts({ sections, emptyText, editLabel }: ContactsProps) {
  return (
    <Layout mainClassName="flex flex-col gap-5 px-4 py-4">
      {sections.length > 0 ? (
        sections.map((section) => (
          <section key={section.heading} className="flex flex-col gap-2.5">
            <h2 className="text-[13px] font-semibold leading-[18px] text-gray-500">
              {section.heading}
            </h2>
            <ul className="flex flex-col gap-2.5">
              {section.contacts.map((contact) => (
                // FR-3.4: the card wrapper is a plain <li>, never a link
                // or any other tappable element — only the pills (and,
                // own-card only, the «Изменить» link) inside it are
                // tappable, so a scroll-tap never dials anyone. FR-7.1:
                // the id every card carries so /profile's own redirect
                // (#contact-<id>) resolves.
                <li key={contact.id} id={`contact-${contact.id}`} className={CARD_CLASSES}>
                  <NameRow contact={contact} editLabel={editLabel} />
                  {contact.note !== null && (
                    <p className="mt-0.5 text-[13px] leading-[18px] text-gray-500">
                      {contact.note}
                    </p>
                  )}
                  {contact.pills.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {contact.pills.map((pill) => (
                        <ContactPill key={pill.label} pill={pill} />
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      ) : (
        <p className="px-6 py-16 text-center text-[15px] text-gray-500">{emptyText}</p>
      )}
    </Layout>
  )
}
